import type { CategoryDto, ItemDto } from '@gl/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState, type ComponentProps } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { FormField } from '@/components/FormField';
import { ImageUpload } from '@/components/ImageUpload';
import { Spinner } from '@/components/FullScreenLoader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { formToCreate, formToUpdate, itemFormSchema, itemToForm, type ItemFormValues } from './itemForm';
import { catalog, useCatalogMutation } from './useCatalog';

export function ItemSheet({
  localId,
  open,
  onOpenChange,
  item,
  defaultCategoryId,
  categories,
}: {
  localId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: ItemDto | null;
  defaultCategoryId: string;
  categories: CategoryDto[];
}) {
  const form = useForm<ItemFormValues>({
    resolver: zodResolver(itemFormSchema),
    defaultValues: itemToForm(item, defaultCategoryId),
  });
  const { errors } = form.formState;
  const variants = useFieldArray({ control: form.control, name: 'variants' });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) form.reset(itemToForm(item, defaultCategoryId));
  }, [open, item, defaultCategoryId, form]);

  const save = useCatalogMutation(
    localId,
    (v: ItemFormValues) =>
      item ? catalog.updateItem(localId, item.id, formToUpdate(v)) : catalog.createItem(localId, formToCreate(v)),
    {
      success: item ? 'Producto guardado' : 'Producto creado',
      onSuccess: () => onOpenChange(false),
    },
  );

  const hasVariants = variants.fields.length > 0;
  const selectable = categories.filter((c) => c.isActive || c.id === item?.categoryId);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 sm:max-w-lg">
        <SheetHeader className="border-b">
          <SheetTitle>{item ? 'Editar producto' : 'Nuevo producto'}</SheetTitle>
          <SheetDescription>Los cambios se ven en tu menú público al guardar.</SheetDescription>
        </SheetHeader>
        <form
          id="item-form"
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
          noValidate
          className="grid flex-1 content-start gap-4 overflow-y-auto p-4"
        >
          <FormField label="Nombre" htmlFor="item-name" error={errors.name?.message}>
            <Input id="item-name" maxLength={80} {...form.register('name')} />
          </FormField>

          <FormField label="Descripción" htmlFor="item-desc" error={errors.description?.message}>
            <Textarea id="item-desc" rows={2} maxLength={500} placeholder="Opcional" {...form.register('description')} />
          </FormField>

          <FormField label="Categoría" error={errors.categoryId?.message}>
            <Controller
              control={form.control}
              name="categoryId"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Elegí una categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectable.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </FormField>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              label={hasVariants ? 'Precio base (opcional)' : 'Precio'}
              htmlFor="item-price"
              error={errors.price?.message}
              hint={hasVariants ? 'Con variantes se muestra el precio de cada una.' : 'En pesos, p. ej. 4500 o 4.500,50'}
            >
              <PesosInput id="item-price" {...form.register('price')} />
            </FormField>
            <FormField label="Costo (opcional)" htmlFor="item-cost" error={errors.cost?.message} hint="No se muestra en el menú.">
              <PesosInput id="item-cost" {...form.register('cost')} />
            </FormField>
          </div>

          <FormField label="Imagen" htmlFor="item-img" error={errors.imageUrl?.message}>
            <Controller
              control={form.control}
              name="imageUrl"
              render={({ field }) => (
                <ImageUpload
                  id="item-img"
                  localId={localId}
                  value={field.value ?? ''}
                  onChange={(url) => field.onChange(url)}
                  error={errors.imageUrl?.message}
                  onBusyChange={setUploading}
                />
              )}
            />
          </FormField>

          <FormField label="Etiquetas" htmlFor="item-tags" error={errors.tags?.message} hint="Separadas por coma, p. ej. sin TACC, vegano">
            <Input id="item-tags" {...form.register('tags')} />
          </FormField>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label htmlFor="item-available">Disponible</Label>
              <p className="text-xs text-muted-foreground">Si lo apagás, se ve atenuado como “no disponible”.</p>
            </div>
            <Controller
              control={form.control}
              name="isAvailable"
              render={({ field }) => (
                <Switch id="item-available" checked={field.value} onCheckedChange={field.onChange} />
              )}
            />
          </div>

          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Variantes</p>
                <p className="text-xs text-muted-foreground">Tamaños o presentaciones con su propio precio (p. ej. 1/4 kg).</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => variants.append({ name: '', price: '', isAvailable: true })}
              >
                <Plus /> Agregar
              </Button>
            </div>
            {errors.variants?.message && <p className="text-xs text-destructive">{errors.variants.message}</p>}
            {variants.fields.map((field, index) => {
              const err = errors.variants?.[index];
              return (
                <div key={field.id} className="grid gap-2 rounded-md border p-2.5">
                  <div className="flex items-start gap-2">
                    <div className="grid flex-1 gap-2 sm:grid-cols-[1fr_8rem]">
                      <div>
                        <Input
                          aria-label="Nombre de la variante"
                          placeholder="Nombre (p. ej. 1/4 kg)"
                          maxLength={80}
                          aria-invalid={!!err?.name}
                          {...form.register(`variants.${index}.name`)}
                        />
                        {err?.name && <p className="mt-1 text-xs text-destructive">{err.name.message}</p>}
                      </div>
                      <div>
                        <PesosInput
                          aria-label="Precio de la variante"
                          placeholder="Precio"
                          aria-invalid={!!err?.price}
                          {...form.register(`variants.${index}.price`)}
                        />
                        {err?.price && <p className="mt-1 text-xs text-destructive">{err.price.message}</p>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm">
                      <Controller
                        control={form.control}
                        name={`variants.${index}.isAvailable`}
                        render={({ field: f }) => <Switch checked={f.value} onCheckedChange={f.onChange} />}
                      />
                      Disponible
                    </label>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        disabled={index === 0}
                        onClick={() => variants.move(index, index - 1)}
                        aria-label="Subir"
                      >
                        <ArrowUp />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        disabled={index === variants.fields.length - 1}
                        onClick={() => variants.move(index, index + 1)}
                        aria-label="Bajar"
                      >
                        <ArrowDown />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8 text-destructive"
                        onClick={() => variants.remove(index)}
                        aria-label="Quitar variante"
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </form>
        <SheetFooter className="flex-row justify-end gap-2 border-t p-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="submit" form="item-form" disabled={save.isPending || uploading}>
            {save.isPending && <Spinner />} Guardar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function PesosInput(props: ComponentProps<typeof Input>) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-muted-foreground">$</span>
      <Input inputMode="decimal" autoComplete="off" className="pl-7" {...props} />
    </div>
  );
}
